/* =====================================================================
   AUDIO — синтез SFX через WebAudio (без файлов).
   Лениво инициализируется на первый пользовательский жест.
   ===================================================================== */

import { Meta } from './meta';

export const Audio2: any = {
  ctx: null,
  master: null,
  enabled: true,
  lastPlay: {},
  volume: 0.7,        // 0..1, базовый множитель громкости
  _lastVol: 0.7,

  ensure() {
    if (this.ctx) return;
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) { this.enabled = false; return; }
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5 * this.volume;
    this.master.connect(this.ctx.destination);
  },

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.volume > 0) this._lastVol = this.volume;
    if (this.master) this.master.gain.value = 0.5 * this.volume;
    if (typeof Meta !== 'undefined') { Meta.data.volume = this.volume; Meta.save(); }
  },
  toggleMute() { this.setVolume(this.volume > 0 ? 0 : this._lastVol); },

  resume() { if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume(); },

  // базовый тон
  tone(freq: number, dur: number, type = 'sine', vol = 0.3, sweep = 0) {
    if (!this.enabled) return;
    this.ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (sweep) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + sweep), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + dur + 0.02);
  },

  noise(dur: number, vol = 0.2, hp = 800) {
    if (!this.enabled) return;
    this.ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const n = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'highpass'; f.frequency.value = hp;
    const g = this.ctx.createGain();
    g.gain.value = vol;
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t);
  },

  // троттлинг частых звуков
  _throttle(key: string, ms: number) {
    const now = (this.ctx ? this.ctx.currentTime * 1000 : performance.now());
    if (this.lastPlay[key] && now - this.lastPlay[key] < ms) return false;
    this.lastPlay[key] = now;
    return true;
  },

  shoot()   { if (this._throttle('shoot', 55)) this.tone(620, 0.07, 'square', 0.10, -260); },
  hit()     { if (this._throttle('hit', 28)) this.tone(180, 0.05, 'square', 0.07, -60); },
  kill()    { if (this._throttle('kill', 30)) this.noise(0.09, 0.10, 1200); },
  pickup()  { if (this._throttle('pickup', 40)) this.tone(880, 0.05, 'triangle', 0.07, 200); },
  levelup() { this.tone(523, 0.10, 'triangle', 0.22, 0); setTimeout(() => this.tone(784, 0.16, 'triangle', 0.22, 0), 90); },
  nova()    { this.tone(140, 0.30, 'sawtooth', 0.20, -90); this.noise(0.25, 0.12, 400); },
  hurt()    { this.tone(150, 0.18, 'sawtooth', 0.22, -80); this.noise(0.12, 0.10, 300); },
  boss()    { this.tone(70, 0.7, 'sawtooth', 0.28, 40); this.tone(110, 0.7, 'square', 0.12, 30); },
  bossDie() { this.tone(90, 0.9, 'sawtooth', 0.3, -50); this.noise(0.8, 0.22, 200); },
  death()   { this.tone(330, 0.6, 'sawtooth', 0.28, -260); this.noise(0.4, 0.15, 200); },
  uiMove()  { this.tone(440, 0.04, 'square', 0.05); },
  uiPick()  { this.tone(660, 0.07, 'triangle', 0.10, 120); },

  /* =====================================================================
     ГЕНЕРАТИВНАЯ МУЗЫКА — процедурный многослойный движок (WebAudio, без файлов).
     Слои: бас-дрон + 3-голосый пэд-аккорд (меняется по прогрессии) + редкие
     мелодические капли. Настроение (mood) реагирует на состояние забега:
     night (база) / deep (глубина) / boss (нагнетание) / dawn (Рассвет).
     Game.musicTick(dt) двигает аккорды/мелодию; Game.musicSetMood меняет палитру.
     ===================================================================== */
  music: null,

  // палитры настроений: корневая нота, темп смены аккордов/мелодии, интервалы аккорда,
  // лад для мелодии (полутона), громкости слоёв, частота фильтра пэда.
  // Тон — НЕ давящий гул, а приятная загадка: открытые квартовые/sus/maj7-созвучия,
  // повыше регистр (меньше мутного суб-баса), мягкая прогрессия без резких скачков.
  _moods: {
    // night — база: парящие квартовые аккорды (root–P4–b7), мягкая загадка
    night: { root: 65.41, chordEvery: 14, leadEvery: 8,  prog: [0, 5, 3, 7, 5, 10],
             padInt: [0, 5, 10], scale: [0, 3, 5, 7, 10], padGain: 0.26, leadGain: 0.13, filt: 620 },
    // deep — глубже/темнее, но мягко: минор с добавленной девятой (root–b3–9)
    deep:  { root: 51.91, chordEvery: 16, leadEvery: 11, prog: [0, 3, -2, 5, 3, 8],
             padInt: [0, 3, 14], scale: [0, 2, 3, 7, 10], padGain: 0.26, leadGain: 0.11, filt: 480 },
    // boss — нагнетание: напряжённые уменьшённые интервалы (root–b3–b5), чаще
    boss:  { root: 49.0,  chordEvery: 8,  leadEvery: 5,  prog: [0, 1, 6, 3, 7, 1],
             padInt: [0, 3, 6],  scale: [0, 1, 3, 6, 7],  padGain: 0.40, leadGain: 0.16, filt: 720 },
    // dawn — рассвет: тёплый мажор7 (root–M3–M7), светло и мечтательно
    dawn:  { root: 65.41, chordEvery: 15, leadEvery: 6,  prog: [0, 4, 7, 9, 5, 2],
             padInt: [0, 4, 11], scale: [0, 2, 4, 7, 11], padGain: 0.26, leadGain: 0.15, filt: 820 },
  },

  startMusic() {
    if (!this.enabled) return;
    this.ensure();
    if (!this.ctx || this.music) return;
    const ctx = this.ctx, t = ctx.currentTime;
    const nodes: any[] = [];                // плоский список ВСЕХ нод для атомарного stopMusic

    const bus = ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.linearRampToValueAtTime(0.55, t + 3);
    bus.connect(this.master);

    // фильтр движения пэда (медленный LFO). Q низкий — мягкое дыхание, а не резонансный «вау».
    const filt = ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 620; filt.Q.value = 1.4;
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.05;
    const lfoG = ctx.createGain(); lfoG.gain.value = 90;
    lfo.connect(lfoG); lfoG.connect(filt.frequency); lfo.start(t);
    filt.connect(bus);

    // мягкая опора-бас (sine, тихо) — основа, а не давящий гул
    const bassG = ctx.createGain(); bassG.gain.value = 0.22; bassG.connect(bus);
    const bass = ctx.createOscillator(); bass.type = 'sine'; bass.frequency.value = 65.41; bass.connect(bassG); bass.start(t);

    // 3-голосый пэд (через фильтр): мягкие тембры + лёгкая расстройка → тёплый шиммер
    const padG = ctx.createGain(); padG.gain.value = 0.26; padG.connect(filt);
    const pad: any[] = [];
    const detune = [-6, 0, 7];
    for (let i = 0; i < 3; i++) {
      const o = ctx.createOscillator(); o.type = i === 1 ? 'triangle' : 'sine';
      o.frequency.value = 130.8; o.detune.value = detune[i];
      o.connect(padG); o.start(t); pad.push(o);
    }

    // «дыхание» пэда: медленный LFO слегка качает громкость аккорда (атмосфера, не статика).
    // Суммируется поверх базовой громкости, которую задаёт _applyMood через setTargetAtTime.
    const breath = ctx.createOscillator(); breath.type = 'sine'; breath.frequency.value = 0.07;
    const breathG = ctx.createGain(); breathG.gain.value = 0.05;
    breath.connect(breathG); breathG.connect(padG.gain); breath.start(t);

    // шина мелодических капель — через bus, чтобы fade-out stopMusic гасил и хвост мелодии
    const leadG = ctx.createGain(); leadG.gain.value = 0.13; leadG.connect(bus);
    // эхо-хвост капель: мягкая обратная связь даёт «загадочное» пространство (как лёгкий реверб)
    const delay = ctx.createDelay(1.0); delay.delayTime.value = 0.38;
    const delayFb = ctx.createGain(); delayFb.gain.value = 0.34;
    const delayMix = ctx.createGain(); delayMix.gain.value = 0.5;
    leadG.connect(delay); delay.connect(delayFb); delayFb.connect(delay);
    delay.connect(delayMix); delayMix.connect(bus);

    nodes.push(lfo, bass, pad[0], pad[1], pad[2], breath);
    this.music = { bus, filt, lfo, lfoG, bass, bassG, pad, padG, breath, breathG,
                   leadG, delay, delayFb, delayMix, nodes,
                   mood: 'night', chordT: 0.4, leadT: 2, progIdx: 0 };
    this._applyMood();
  },

  // сменить настроение музыки (вызывает Game на боссе/глубине/Рассвете)
  musicSetMood(mood: string) {
    if (!this.music || !this._moods[mood]) return;
    if (this.music.mood === mood) return;
    this.music.mood = mood;
    this.music.chordT = Math.min(this.music.chordT, 1.5);   // быстрее перейти на новую палитру
    this._applyMood();
  },

  _applyMood() {
    const m = this.music; if (!m || !this.ctx) return;
    const md = this._moods[m.mood] || this._moods.night;
    const t = this.ctx.currentTime;
    try {
      m.padG.gain.cancelScheduledValues(t); m.padG.gain.setTargetAtTime(md.padGain, t, 1.2);
      m.leadG.gain.cancelScheduledValues(t); m.leadG.gain.setTargetAtTime(md.leadGain, t, 1.2);
      m.filt.frequency.cancelScheduledValues(t); m.filt.frequency.setTargetAtTime(md.filt, t, 1.5);
    } catch (e) {}
  },

  // двигатель: смена аккордов по прогрессии + редкие мелодические капли
  musicTick(dt: number) {
    const m = this.music; if (!m || !this.ctx) return;
    const md = this._moods[m.mood] || this._moods.night;
    m.chordT -= dt;
    if (m.chordT <= 0) { m.chordT += md.chordEvery; this._nextChord(); }
    m.leadT -= dt;
    if (m.leadT <= 0) { m.leadT += md.leadEvery * (0.6 + Math.random() * 0.9); this._leadNote(); }
  },

  _nextChord() {
    const m = this.music; if (!m || !this.ctx) return;
    const md = this._moods[m.mood] || this._moods.night;
    const t = this.ctx.currentTime;
    m.progIdx = (m.progIdx + 1) % md.prog.length;
    const rootSemi = md.prog[m.progIdx];
    const root = md.root * Math.pow(2, rootSemi / 12);
    try {
      m.bass.frequency.setTargetAtTime(root, t, 0.8);
      for (let i = 0; i < m.pad.length; i++) {
        const f = root * 2 * Math.pow(2, (md.padInt[i] || 0) / 12);   // пэд октавой выше баса
        m.pad[i].frequency.setTargetAtTime(f, t, 1.4);
      }
    } catch (e) {}
  },

  _leadNote() {
    const m = this.music; if (!m || !this.ctx) return;
    const md = this._moods[m.mood] || this._moods.night;
    const t = this.ctx.currentTime;
    const rootSemi = md.prog[m.progIdx];
    const deg = md.scale[Math.floor(Math.random() * md.scale.length)];
    const oct = 3 + Math.floor(Math.random() * 2);                    // высоко, «капля света»
    const freq = md.root * Math.pow(2, (rootSemi + deg) / 12 + oct);
    try {
      // мягкая капля-«колокольчик»: чистый sine, плавная атака и долгий хвост (+ эхо через leadG→delay)
      const o = this.ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freq;
      const g = this.ctx.createGain(); g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.42, t + 0.07);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 2.0);
      o.connect(g); g.connect(m.leadG);
      o.start(t); o.stop(t + 2.1);
    } catch (e) {}
  },

  stopMusic() {
    if (!this.music || !this.ctx) { this.music = null; return; }
    const t = this.ctx.currentTime;
    const m = this.music; this.music = null;
    try {
      m.bus.gain.cancelScheduledValues(t);
      m.bus.gain.setValueAtTime(m.bus.gain.value, t);
      m.bus.gain.linearRampToValueAtTime(0.0001, t + 0.7);
      for (const n of m.nodes) { try { n.stop(t + 0.8); } catch (e) {} }   // плоский список — структура нод не важна
    } catch (e) {}
  },
};
